/**
 * The background half of a Brain save/delete: run the indexing request, and when
 * the server only answers "still working" (it stops waiting after 45 s while the
 * executor keeps going), keep asking the index until it has caught up — so the
 * user is told how it really ended instead of being left to guess.
 *
 * Pure on purpose (no React, no antd, no fetch): every effect comes in through
 * `IndexingJobDeps`, so the decision of what counts as "done" is unit tested.
 */
import type { BrainIndexingResult, BrainPathIndexState } from '../types';

export type IndexingJobAction = 'index' | 'deindex';

export interface IndexingJobSpec {
  action: IndexingJobAction;
  /** What the user calls it: a document or folder name, shown in the result. */
  label: string;
  /** Documents to index (exactly one) or to remove from the index (one or many). */
  paths: string[];
  /** `sha` of the saved version (what the save returned), for `index`. */
  sha?: string | null;
}

export type IndexingOutcome =
  | { kind: 'success'; chunks?: number; entities?: number; relationships?: number }
  | { kind: 'failed'; errors: string[] }
  /** The server stopped waiting and the index did not confirm in time: neither success nor failure. */
  | { kind: 'unconfirmed' };

export interface IndexingJobDeps {
  run(spec: IndexingJobSpec): Promise<BrainIndexingResult>;
  state(path: string): Promise<BrainPathIndexState>;
  sleep(ms: number): Promise<void>;
  now(): number;
}

export interface IndexingJobOptions {
  pollIntervalMs?: number;
  maxWaitMs?: number;
  /** A folder delete can cover many documents: confirm on a sample, not on every one. */
  maxPolledPaths?: number;
}

const DEFAULTS: Required<IndexingJobOptions> = {
  pollIntervalMs: 3_000,
  maxWaitMs: 4 * 60_000,
  maxPolledPaths: 5,
};

/** The server and the executor stamp times on different machines. */
const CLOCK_SKEW_MS = 5_000;

function isConfirmed(spec: IndexingJobSpec, states: BrainPathIndexState[], startedAtMs: number): boolean {
  if (states.length === 0) return false;
  if (spec.action === 'deindex') return states.every((state) => !state.indexed);
  const [state] = states;
  if (!state?.indexed) return false;
  // Same hash as the save: exactly this version is indexed (and an unchanged
  // document is confirmed at once, whoever indexed it).
  if (spec.sha && state.content_sha) return state.content_sha === spec.sha;
  // An index is only done once chunks NEWER than this request exist: the chunks
  // of the previous version of the document do not count.
  if (!state.indexed_at) return false;
  return Date.parse(state.indexed_at) >= startedAtMs - CLOCK_SKEW_MS;
}

export async function runIndexingJob(
  spec: IndexingJobSpec,
  deps: IndexingJobDeps,
  options: IndexingJobOptions = {},
): Promise<IndexingOutcome> {
  const { pollIntervalMs, maxWaitMs, maxPolledPaths } = { ...DEFAULTS, ...options };
  const requestedAtMs = deps.now();

  let result: BrainIndexingResult;
  try {
    result = await deps.run(spec);
  } catch (error) {
    return { kind: 'failed', errors: [error instanceof Error ? error.message : 'Error desconocido'] };
  }

  if (result.errors.length > 0) return { kind: 'failed', errors: result.errors };
  if (!result.pending) {
    return { kind: 'success', chunks: result.chunks, entities: result.entities, relationships: result.relationships };
  }

  // Pending: poll the index until it reflects this change, or give up honestly.
  // The server says which version the job indexed; a newer save may have landed
  // first, and then the saved `sha` of this spec would never show up.
  const target: IndexingJobSpec = result.sha ? { ...spec, sha: result.sha } : spec;
  const startedAtMs = result.startedAt ? Date.parse(result.startedAt) : requestedAtMs;
  const polled = spec.action === 'index' ? spec.paths.slice(0, 1) : spec.paths.slice(0, maxPolledPaths);
  const deadline = deps.now() + maxWaitMs;

  while (deps.now() < deadline) {
    await deps.sleep(pollIntervalMs);
    try {
      const states = await Promise.all(polled.map((path) => deps.state(path)));
      if (isConfirmed(target, states, startedAtMs)) return { kind: 'success' };
    } catch {
      // A failed status check is not a failed job: ask again on the next round.
    }
  }
  return { kind: 'unconfirmed' };
}
